import PropTypes from 'prop-types';
import Modal from '../../../../components/Modal';
import { Button, Col, OverlayTrigger, Row, Tooltip } from 'react-bootstrap';
import AssignmentSettings from '../../../../components/AssignmentSettings';
import { useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import CustomToast from '../../../../components/Toast';
import { useAssignmentContext } from '../../context';
import moment from 'moment';
import { faUsers, faCopy } from '@fortawesome/free-solid-svg-icons';
import { useGetUserIdMutation } from '../../../../features/authentication/dashboardHeaderApiSlice';
import { useCreateAssignmentLinkMutation } from '../../../../features/authentication/assignApiSlice';
import GoogleClassroomShareModal from '../../../../components/GoogleClassroomShareModal';

const CreateAssessmentLinkModal = ({ isOpen, onClose, selectedAssessments }) => {
  const [contentNote, setContentNote] = useState('');
  const [createLinkIsLoading, setCreateLinkIsLoading] = useState(false);
  const [isOpenshowLinkModal, setIsOpenShowLinkModal] = useState(false);
  const [isOpenShareToGoogleClassroomModal, setIsOpenShareToGoogleClassroomModal] = useState(false);
  const [shareToGoogleGuid, setShareToGoogleGuid] = useState(null);
  const [shareToGoogleAssignmentName, setShareToGoogleAssignmentName] = useState(null);

  const assignmentContext = useAssignmentContext();
  const createdAssessmentLink = useRef('');

  const [getUserId, userIdResults] = useGetUserIdMutation();
  const [createAssignmentLinkCall] = useCreateAssignmentLinkMutation();

  useEffect(() => {
    getUserId();
  }, []);

  const handleOnClose = () => {
    setContentNote('');
    setShareToGoogleGuid(null);
    setShareToGoogleAssignmentName(null);
    onClose();
  };

  const handleOnChange = (e) => {
    setContentNote(e.target.value);
  };

  async function createAssignmentLink() {
    const timedTimeSpan =
      parseInt(assignmentContext.timedHour) * 60 + parseInt(assignmentContext.timedMinute);
    var selfStudentModel = [{ studentId: userIdResults.data.userId, classId: -1 }];

    if (!assignmentContext.assignmentMode) {
      CustomToast('Please select the assignment mode', 'warning');
      return;
    }
    if (assignmentContext.isTimed && timedTimeSpan <= 0) {
      CustomToast('The time length must be greater than 0 minutes.', 'info');
      return;
    }

    if (assignmentContext.availabilityOption == 'FromTo') {
      const currentDate = moment().format('YYYY/MM/DD h:mm A');
      if (moment(assignmentContext.startDate).isBefore(currentDate, 'day')) {
        CustomToast('From Date must not be before the current day', 'error');
        return;
      }
      if (moment(assignmentContext.endDate).isBefore(currentDate)) {
        CustomToast('To Date must not be before the current date/time', 'error');
        return;
      }
      if (moment(assignmentContext.endDate).isBefore(moment(assignmentContext.startDate)) || moment(assignmentContext.endDate).isSame(moment(assignmentContext.startDate))) {
        CustomToast('To Date must be after the From Date', 'error');
        return;
      }
    }

    if (assignmentContext.retakesEnabled) {
      if (assignmentContext.retakeLimitValue <= 0) {
        CustomToast('You must select a retake limit', 'info');
        return;
      }
      if (assignmentContext.retakeQuestion === null) {
        CustomToast(
          'You must select an option for Auto Graded Questions to include on Retakes',
          'info'
        );
        return;
      }
      if (assignmentContext.includeCR === null) {
        CustomToast('You must select an option for CR Questions to include on Retakes', 'info');
        return;
      }
    }
    setCreateLinkIsLoading(true);

    try {
      const options = {
        assignmentIds: selectedAssessments?.map(a => a.assignmentId),
        students: selfStudentModel,
        selfAssign: false,
        availability: assignmentContext.availabilityOption,
        assignmentMode: assignmentContext.assignmentMode,
        allowAnswerChange: assignmentContext.allowAnswerChange,
        showGrade: assignmentContext.showGrade,
        startDate: assignmentContext.startDate != '' ? moment(assignmentContext.startDate).format('YYYY/MM/DD h:mm A') : '',
        endDate: assignmentContext.endDate != '' ? moment(assignmentContext.endDate).format('YYYY/MM/DD h:mm A') : '',
        studentFileUpload: assignmentContext.studentFileUpload,
        timed: assignmentContext.isTimed,
        timeSpan: timedTimeSpan,
        practice: true,
        assignRetake: assignmentContext.retakesEnabled,
        masteryLevel: assignmentContext.masteryValue,
        retakeMode: assignmentContext.assignmentMode,
        scoreRetries: assignmentContext.scoreRetries,
        retakeLimit: assignmentContext.retakeLimitValue,
        retakeAvailability: assignmentContext.availabilityOption,
        retakeStartDate: assignmentContext.startDate,
        retakeEndDate: assignmentContext.endDate,
        retakeTimed: assignmentContext.isTimed,
        retakeTimeSpan: timedTimeSpan,
        retakeQuestion: assignmentContext.retakeQuestion,
        includeCR: assignmentContext.includeCR,
        randomized: assignmentContext.randomized,
        textToSpeech: assignmentContext.textToSpeech,
        autoLockQuestions: assignmentContext.autoLockQuestions,
        allowLangChange: assignmentContext.allowLangChange,
        useCalc: assignmentContext.calculatorEnabled ? 1 : 0,
        useProtractor: assignmentContext.useProtractor ? 1 : 0,
        useRuler: assignmentContext.useRuler ? 1 : 0,
        timeOnTask: assignmentContext.timeOnTaskEnabled,
        period: assignmentContext.timeOnTaskSeconds,
        lockBrowser: assignmentContext.lockBrowser,
        photoMathBlock: assignmentContext.photoMathBlock,
        autoStartCRUponGrading: assignmentContext.autoStartCRUponGrading,
        cranswerToolId: assignmentContext.crAnswerToolType,
        calculatorType: assignmentContext.calculatorType,
        notes: contentNote
      };
      assignmentContext.setAssignmentOptions(options);
      createdAssessmentLink.current = await createAssignmentLinkCall(options).unwrap();

      if (createdAssessmentLink.current.length > 1) {
        CustomToast(
          'Assessment links have been created, you can find them under "View Assessment Links"',
          'success'
        );
        handleOnClose();
      } else if (createdAssessmentLink.current.length === 1) {
        setShareToGoogleGuid(createdAssessmentLink.current[0]);
        setShareToGoogleAssignmentName(selectedAssessments[0].assignmentName);
        setCreateLinkIsLoading(false);
        setIsOpenShowLinkModal(true);
      } else {
        CustomToast('Error! Couldn\'t create assessment link', 'error');
      }
    } catch (ex) {
      CustomToast('Error! Couldn\'t create assessment link', 'error');
      setCreateLinkIsLoading(false);
    }
  }

  function copyLinkToClipboard(fullUrl) {
    navigator.clipboard.writeText(fullUrl);
    CustomToast('Link copied', 'info');
  }

  const handleAssessmentLinkOnClose = () => {
    setIsOpenShowLinkModal(false);
    handleOnClose();
  };

  return (
    <>
      <Modal
        contentWidth={'100%'}
        isOpen={isOpen}
        titleName={'Create Assessment Link'}
        onClose={() => handleOnClose()}
      >
        <Col>
          <Row>
            <AssignmentSettings
              allowRetakesOption={false}
              headingDescription={false}
              availabilityRequired={true}
              modesRequired={true}
              showBetaButton={true}
              noColumn={false}
              twoColumn={true}
              randomizedQuestion={true}
              disabledTextSpeach={true}
              allowOfflineMode={true}
              isSelfAssign={false}
              isAssessment={true}
            />
          </Row>
          <Row className='p-4'>
            <div className='pb-2'>
              Notes
            </div>
            <textarea
              type="text"
              className="form-control textarea-lg"
              placeholder=" "
              name="noteDescription"
              id="noteDescription"
              value={contentNote}
              onChange={handleOnChange}
            />
          </Row>
          <div className='d-flex justify-content-end mt-3 gap-3 align-items-center'>
            <button
              className="btn btn-castleGray print-btn"
              onClick={() => handleOnClose()}
            >
              Cancel
            </button>
            <Button
              variant="castleRed"
              onClick={() => createAssignmentLink()}
              disabled={createLinkIsLoading}>
              {createLinkIsLoading ? 'Creating Link...' : 'Create Link'}
            </Button>
          </div>
        </Col>
      </Modal>

      <Modal
        contentWidth="50%"
        isOpen={isOpenshowLinkModal}
        titleName={'Assessment Link Created'}
        onClose={() => handleAssessmentLinkOnClose()}
      >
        <div className="d-flex flex-column align-items-center">
          <div className='text-center pb-5'>
            Click
            <OverlayTrigger
              key="copyToClipboard"
              placement="top"
              overlay={<Tooltip className="custom-tooltip">Copy to Clipboard</Tooltip>}>
              <button
                className="copyLinkBtn mx-2"
                onClick={() => copyLinkToClipboard(createdAssessmentLink.current[0])}>
                <FontAwesomeIcon icon={faCopy} />
              </button>
            </OverlayTrigger>
            Copy to clipboard to copy to clipboard, then paste where you want to use it
            <br />
            or click
            <OverlayTrigger
              key="shareToGoogleClassroom"
              placement="top"
              overlay={<Tooltip className="custom-tooltip">Share to Google Classroom</Tooltip>}>
              <button
                className="shareToClassroomBtn mx-2"
                onClick={() => {
                  setIsOpenShareToGoogleClassroomModal(true);
                }}
              >
                <FontAwesomeIcon icon={faUsers} />
              </button>
            </OverlayTrigger>
            Share to Google Classroom to share to Google Classroom
          </div>
          <div>You can also find this link under &quot;View Assessment Links&quot;.</div>
        </div>
      </Modal>

      <GoogleClassroomShareModal
        showShareToGoogleClassroomModal={isOpenShareToGoogleClassroomModal}
        setShowShareToGoogleClassroomModal={setIsOpenShareToGoogleClassroomModal}
        urlGuid={shareToGoogleGuid}
        assignmentName={shareToGoogleAssignmentName}
      />
    </>
  );
};

CreateAssessmentLinkModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  selectedAssessments: PropTypes.array.isRequired
};

export default CreateAssessmentLinkModal;
